import { defineStore } from 'pinia';
import { ref } from 'vue';
import { pollProjectSorAnalyzeResult } from '@/api/projectApi';
import type { SorAnalyzeStepStatus } from '@/utils/sorAnalyzeAiSteps';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';

export type SorAnalyzePollHandlers = {
  onProcessing: (data: Record<string, unknown>, res: Record<string, unknown>) => void | Promise<void>;
  onCompleted: (data: Record<string, unknown>, res: Record<string, unknown>) => void | Promise<void>;
  onFailed: (message: string) => void;
  onConnectionLost?: (message: string) => void;
};

const POLL_INTERVAL_MS = 2000;
/** 瞬时网络错误（断网/代理闪断）最多连续重试次数，避免直接终态失败 */
const TRANSIENT_NETWORK_RETRY_MAX = 5;
const TRANSIENT_NETWORK_RETRY_BASE_MS = 1500;

function isTransientNetworkError(err: unknown): boolean {
  const e = err as { message?: string; code?: string; response?: unknown };
  if (e?.response != null) return false;
  const msg = String(e?.message || err || '').toLowerCase();
  const code = String(e?.code || '').toLowerCase();
  return (
    msg.includes('network error')
    || msg.includes('failed to fetch')
    || msg.includes('timeout')
    || msg.includes('econnaborted')
    || msg.includes('econnreset')
    || code === 'err_network'
    || code === 'econnaborted'
  );
}

export const useSorAnalyzePollStore = defineStore('sorAnalyzePoll', () => {
  const activeProjectId = ref<number | null>(null);
  const activeTaskId = ref('');
  const pollingPaused = ref(false);
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let handlers: SorAnalyzePollHandlers | null = null;
  let tickingGeneration: number | null = null;
  let transientNetworkFailures = 0;
  let epoch = 0;

  function isPolling(projectId?: number, taskId?: string): boolean {
    if (!activeTaskId.value) return false;
    if (taskId && activeTaskId.value !== taskId) return false;
    if (projectId != null && activeProjectId.value !== projectId) return false;
    return !!pollTimer;
  }

  function stop() {
    epoch++;
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
    handlers = null;
    activeProjectId.value = null;
    activeTaskId.value = '';
    pollingPaused.value = false;
    transientNetworkFailures = 0;
  }

  async function tickWithNetworkRetry(
    projectId: number,
    taskId: string,
    h: SorAnalyzePollHandlers,
    generation: number,
  ): Promise<'processing' | 'completed' | 'failed' | 'retrying'> {
    try {
      const outcome = await pollOnce(projectId, taskId, h, generation);
      if (generation === epoch) transientNetworkFailures = 0;
      return outcome;
    } catch (e: unknown) {
      if (generation !== epoch) return 'failed';
      if (isTransientNetworkError(e) && transientNetworkFailures < TRANSIENT_NETWORK_RETRY_MAX) {
        transientNetworkFailures += 1;
        const delay = TRANSIENT_NETWORK_RETRY_BASE_MS * transientNetworkFailures;
        await new Promise((r) => setTimeout(r, delay));
        return 'retrying';
      }
      const err = e as { message?: string };
      (h.onConnectionLost || h.onFailed)(err?.message || '轮询失败');
      return 'failed';
    }
  }

  function pausePolling() {
    if (!activeTaskId.value) return;
    pollingPaused.value = true;
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  }

  function resumePolling() {
    if (!pollingPaused.value || !handlers || activeProjectId.value == null || !activeTaskId.value) {
      return;
    }
    pollingPaused.value = false;
    const projectId = activeProjectId.value;
    const taskId = activeTaskId.value;
    const h = handlers;
    const generation = ++epoch;

    const tick = async () => {
      if (tickingGeneration === generation || !handlers || pollingPaused.value || generation !== epoch) return;
      tickingGeneration = generation;
      try {
        const outcome = await tickWithNetworkRetry(projectId, taskId, h, generation);
        if (generation === epoch && (outcome === 'completed' || outcome === 'failed')) {
          stop();
        }
      } finally {
        if (tickingGeneration === generation) tickingGeneration = null;
      }
    };

    void tick();
    if (!pollTimer) {
      pollTimer = setInterval(() => {
        void tick();
      }, POLL_INTERVAL_MS);
    }
  }

  async function pollOnce(
    projectId: number,
    taskId: string,
    h: SorAnalyzePollHandlers,
    generation: number,
  ): Promise<'processing' | 'completed' | 'failed'> {
    const res = (await pollProjectSorAnalyzeResult(taskId, projectId)) as Record<string, unknown>;
    if (generation !== epoch) return 'failed';
    const data = (res?.data || null) as Record<string, unknown> | null;
    const status = String(data?.status || '');
    const pickMessage = (...candidates: unknown[]) => {
      for (const c of candidates) {
        const s = String(c ?? '').trim();
        if (!s) continue;
        if (s.toLowerCase() === 'success' || s === '操作成功') continue;
        return s;
      }
      return '';
    };

    // 即使外层 success=false，只要 data.status 可识别，仍走状态机（避免只看到占位 success）
    if (data && (status === 'processing' || status === 'completed' || status === 'failed')) {
      if (status === 'processing') {
        await h.onProcessing(data, res);
        return 'processing';
      }
      if (status === 'failed') {
        h.onFailed(pickMessage(data.message, res.message) || '解析失败');
        return 'failed';
      }
      await h.onCompleted(data, res);
      return 'completed';
    }

    if (!res?.success || !data) {
      throw new Error(pickMessage(res?.message, data?.message) || '轮询失败');
    }
    await h.onCompleted(data, res);
    return 'completed';
  }

  function setHandlers(h: SorAnalyzePollHandlers | null) {
    handlers = h;
  }

  function start(projectId: number, taskId: string, h: SorAnalyzePollHandlers) {
    activeProjectId.value = projectId;
    activeTaskId.value = taskId;
    handlers = h;
    const generation = ++epoch;

    const tick = async () => {
      if (tickingGeneration === generation || !handlers || pollingPaused.value || generation !== epoch) return;
      tickingGeneration = generation;
      try {
        const outcome = await tickWithNetworkRetry(projectId, taskId, handlers, generation);
        if (generation === epoch && (outcome === 'completed' || outcome === 'failed')) {
          stop();
        }
      } finally {
        if (tickingGeneration === generation) tickingGeneration = null;
      }
    };

    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }

    void tick();
    pollTimer = setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);
  }

  /** 单次查询任务状态（用于页面恢复时判断 processing / completed） */
  async function fetchStatus(projectId: number, taskId: string) {
    const res = (await pollProjectSorAnalyzeResult(taskId, projectId)) as Record<string, unknown>;
    if (!res?.success || !res?.data) {
      throw new Error(String(res?.message || '轮询失败'));
    }
    return { res, data: res.data as Record<string, unknown> };
  }

  return {
    activeProjectId,
    activeTaskId,
    pollingPaused,
    isPolling,
    start,
    setHandlers,
    stop,
    pausePolling,
    resumePolling,
    fetchStatus,
  };
});

export type SorAnalyzeTaskPersist = {
  taskId: string;
  completed?: boolean;
  baseRevision?: number;
  sourceObjectKey?: string;
  startedAt?: number;
  steps?: SorAnalyzeStepStatus[];
  sessionId?: string;
  streamedCount?: number;
  /** 嵌入模式：当前写入批次 ID */
  targetBatchId?: number;
  /** 重新生成时需移除的旧批次 ID（仅影响对应文档） */
  replaceBatchIds?: number[];
  appendMode?: boolean;
  /** 非嵌入单文档：整表替换 */
  fullTableReplace?: boolean;
};

export function sorAnalyzeTaskStorageKey(projectId: number): string {
  return `proj-sor-analyze-task:${projectId}${getUserSessionScopeSuffix()}`;
}

export function loadSorAnalyzeTaskPersist(projectId: number): SorAnalyzeTaskPersist | null {
  try {
    const raw = window.localStorage.getItem(sorAnalyzeTaskStorageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SorAnalyzeTaskPersist;
    return parsed?.taskId ? parsed : null;
  } catch {
    return null;
  }
}

export function saveSorAnalyzeTaskPersist(projectId: number, payload: SorAnalyzeTaskPersist) {
  window.localStorage.setItem(sorAnalyzeTaskStorageKey(projectId), JSON.stringify(payload));
}

export function clearSorAnalyzeTaskPersist(projectId: number) {
  window.localStorage.removeItem(sorAnalyzeTaskStorageKey(projectId));
}
